/**
 * Issued invoices.
 *
 * Each one is a snapshot. Its lines were frozen when it was raised, so
 * downloading a six-month-old invoice produces the document that was actually
 * sent — not a re-render of whatever the time entries say today.
 *
 * That is also why "released" rows are worth showing. Un-archiving a row hands
 * the work back to the billable list without touching the invoice it came from;
 * an invoice showing 12 lines of which 3 are released is telling you those
 * three hours are queued to be billed again.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '@/api/client'
import type { ConsultingInvoice } from '@/api/types'
import { useToast } from '@/components/Toast'
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui'
import { formatDateOnly } from '@/lib/format'
import { formatHours, formatMoney, formatWorkDate, rateLabel, trimHours } from '@/lib/consulting'

export function ConsultingInvoicesPage() {
  const toast = useToast()
  const [invoices, setInvoices] = useState<ConsultingInvoice[]>([])
  const [expanded, setExpanded] = useState<ConsultingInvoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setInvoices(await api.consulting.invoices.list())
      setError(null)
    } catch (caught) {
      if (caught instanceof ApiError && !caught.isUnauthorized) setError(caught.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function open(invoice: ConsultingInvoice) {
    if (expanded?.id === invoice.id) {
      setExpanded(null)
      return
    }
    try {
      // The list omits the lines; a screen showing forty invoices has no use
      // for four hundred rows it is not displaying.
      setExpanded(await api.consulting.invoices.get(invoice.id))
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not open that.')
    }
  }

  async function remove(invoice: ConsultingInvoice) {
    try {
      const result = await api.consulting.invoices.remove(invoice.id)
      if (expanded?.id === invoice.id) setExpanded(null)
      await load()
      toast.success(result.message)
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not delete that.')
    }
  }

  if (loading) return <LoadingBlock label="Loading invoices" />
  if (error) return <ErrorState message={error} onRetry={() => void load()} />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-bold">Invoices</h1>
        <Link to="/consulting" className="btn btn-neutral btn-sm ml-auto">
          Back to time
        </Link>
      </div>

      {invoices.length === 0 ? (
        <section className="card overflow-hidden">
          <EmptyState
            title="No invoices yet"
            action={
              <Link to="/consulting" className="btn btn-primary">
                Go and bill something
              </Link>
            }
          >
            Tick the rows you want to bill on the Consulting tab and create an invoice from
            them. They move here, and the Word document is a download away.
          </EmptyState>
        </section>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {invoices.map((invoice) => {
            const released = invoice.line_count - invoice.archived_day_count
            const isOpen = expanded?.id === invoice.id
            return (
              <li key={invoice.id} className="card overflow-hidden">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5">
                  <span className="font-semibold text-navy">#{invoice.id}</span>
                  <span className="font-medium">{invoice.bill_to_name}</span>
                  <span className="text-xs text-ink-3">{invoice.period_label}</span>
                  <span className="text-xs text-ink-3">
                    {formatDateOnly(invoice.invoice_date)}
                  </span>
                  <span className="pill bg-navy-soft text-navy tabular-nums">
                    {trimHours(invoice.total_hours)} h
                  </span>
                  <span className="font-semibold tabular-nums">
                    {formatMoney(invoice.total_amount)}
                  </span>
                  {released > 0 && (
                    <span
                      className="pill bg-warning-light text-warning-text"
                      title="Released rows are billable again; this invoice still lists them"
                    >
                      {released} released
                    </span>
                  )}

                  <span className="ml-auto flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-neutral btn-sm"
                      onClick={() => void open(invoice)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? 'Hide' : 'Lines'}
                    </button>
                    <a
                      className="btn btn-primary btn-sm"
                      href={api.consulting.invoices.downloadUrl(invoice.id)}
                    >
                      Download
                    </a>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => void remove(invoice)}
                      title="Deletes the document and hands its rows back to the billable list"
                    >
                      Delete
                    </button>
                  </span>
                </div>

                {isOpen && expanded && <InvoiceLines invoice={expanded} />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function InvoiceLines({ invoice }: { invoice: ConsultingInvoice }) {
  return (
    <div className="border-t border-line bg-surface-2 px-3 py-2">
      {invoice.re_line && (
        <p className="mb-2 text-xs text-ink-3">
          Re: <span className="text-ink-2">{invoice.re_line}</span>
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-lg border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
              <th scope="col" className="py-1 pr-3 font-semibold">
                Date
              </th>
              <th scope="col" className="py-1 pr-3 font-semibold">
                Client
              </th>
              <th scope="col" className="py-1 pr-3 font-semibold">
                Activity
              </th>
              <th scope="col" className="py-1 pr-3 text-right font-semibold">
                Rate
              </th>
              <th scope="col" className="py-1 text-right font-semibold">
                Time
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line) => (
              <tr key={line.id} className="border-t border-line">
                <td className="py-1 pr-3 whitespace-nowrap tabular-nums">
                  {formatWorkDate(line.work_date)}
                </td>
                <td className="py-1 pr-3 whitespace-nowrap">{line.client_name}</td>
                <td className="py-1 pr-3">{line.activity}</td>
                <td className="py-1 pr-3 text-right whitespace-nowrap">
                  {rateLabel(line.rate_kind, line.rate, true)}
                </td>
                <td className="py-1 text-right tabular-nums">{formatHours(line.hours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
